import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ArrowLeft,
    Send,
    Mail,
    MessageCircle,
    CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';
import { CoRegulateLogo } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Contact = () => {
    const navigate = useNavigate();
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [reference, setReference] = useState('');
    
    const [form, setForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!form.name || !form.email || !form.subject || !form.message) {
            toast.error('Please fill in all fields');
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/api/support/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });

            if (response.ok) {
                const data = await response.json();
                setReference(data.reference);
                setSubmitted(true);
            } else {
                toast.error('Failed to send message');
            }
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <IconContainer variant="sage" size="xl" useGradient={true} className="mx-auto mb-6">
                        <CheckCircle className="w-10 h-10" />
                    </IconContainer>
                    <Heading as="h1" size="text-2xl" className="mb-3">Message Sent</Heading>
                    <BodyText className="text-[#7D8E95] mb-2">
                        Thank you for reaching out. We'll get back to you as soon as possible.
                    </BodyText>
                    {reference && (
                        <SmallText className="text-[#7D8E95] mb-8">
                            Reference: #{reference}
                        </SmallText>
                    )}
                    <PrimaryButton onClick={() => navigate('/')}>
                        Return Home
                    </PrimaryButton>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header */}
            <header className="glass-header">
                <Container size="md" className="py-4 flex items-center gap-4">
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <CoRegulateLogo size="sm" />
                </Container>
            </header>

            <main className="max-w-xl mx-auto px-4 py-12">
                {/* Header */}
                <div className="text-center mb-10">
                    <IconContainer variant="sage" size="lg" useGradient={true} className="mx-auto mb-4">
                        <Mail className="w-6 h-6" />
                    </IconContainer>
                    <Heading as="h1" size="text-2xl" className="mb-2">Get in Touch</Heading>
                    <BodyText className="text-[#7D8E95]">
                        Have a question or feedback? We'd love to hear from you.
                    </BodyText>
                </div>

                {/* Direct Email Options */}
                <div className="bg-white/70 rounded-xl p-5 mb-8 border border-white/40/30">
                    <SmallText className="text-[#7D8E95] mb-3 block">
                        <strong className="text-[#1D6B6B]">Email us directly:</strong>
                    </SmallText>
                    <div className="space-y-2">
                        <a 
                            href="mailto:hello@coregulateadhd.com"
                            className="flex flex-col sm:flex-row sm:items-center gap-1 text-sm"
                        >
                            <span className="font-medium text-[#1D6B6B] hover:underline">hello@coregulateadhd.com</span>
                            <span className="text-[#7D8E95]">— General enquiries & feedback</span>
                        </a>
                        <a 
                            href="mailto:support@coregulateadhd.com"
                            className="flex flex-col sm:flex-row sm:items-center gap-1 text-sm"
                        >
                            <span className="font-medium text-[#1D6B6B] hover:underline">support@coregulateadhd.com</span>
                            <span className="text-[#7D8E95]">— Billing & technical issues</span>
                        </a>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm text-[#7D8E95] mb-2 block">Name</label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({...form, name: e.target.value})}
                                placeholder="Your name"
                                className="w-full p-3 bg-white border border-white/40 rounded-xl focus:outline-none focus:border-[#1D6B6B]"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-sm text-[#7D8E95] mb-2 block">Email</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({...form, email: e.target.value})}
                                placeholder="your@email.com"
                                className="w-full p-3 bg-white border border-white/40 rounded-xl focus:outline-none focus:border-[#1D6B6B]"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm text-[#7D8E95] mb-2 block">Subject</label>
                        <input
                            type="text"
                            value={form.subject}
                            onChange={(e) => setForm({...form, subject: e.target.value})}
                            placeholder="What's this about?"
                            className="w-full p-3 bg-white border border-white/40 rounded-xl focus:outline-none focus:border-[#1D6B6B]"
                            required
                        />
                    </div>

                    <div>
                        <label className="text-sm text-[#7D8E95] mb-2 block">Message</label>
                        <textarea
                            value={form.message}
                            onChange={(e) => setForm({...form, message: e.target.value})}
                            placeholder="Tell us more..."
                            className="w-full p-3 bg-white border border-white/40 rounded-xl resize-none focus:outline-none focus:border-[#1D6B6B]"
                            rows={6}
                            required
                        />
                    </div>

                    <PrimaryButton type="submit" disabled={submitting} className="w-full" size="lg">
                        <Send className="w-4 h-4 mr-2" />
                        {submitting ? 'Sending...' : 'Send Message'}
                    </PrimaryButton>
                </form>

                {/* Footer note */}
                <div className="mt-8 text-center">
                    <SmallText className="text-[#7D8E95]">
                        Already have an account?{' '}
                        <button onClick={() => navigate('/auth')} className="text-[#1D6B6B] hover:underline">
                            Sign in
                        </button>
                        {' '}to create a support ticket for faster responses.
                    </SmallText>
                </div>
            </main>
        </div>
    );
};

export default Contact;
